import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Megaphone, Calendar, Search } from "lucide-react";
import { api, resolveImage } from "@/lib/api";

export default function NoticeBoard() {
  const [q, setQ] = useState("");
  const { data: notices, isLoading } = useQuery({
    queryKey: ["notices"],
    queryFn: async () => (await api.get("/notices")).data,
  });

  const filtered = (notices || []).filter(
    (n) => n.title.toLowerCase().includes(q.toLowerCase()) || n.description.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-12">
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Announcements</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold text-white sm:text-5xl">Notice Board</h1>
        <p className="mt-3 text-slate-400">Latest updates, alerts and important information from Golden Live.</p>
      </div>

      <div className="relative mx-auto mt-8 max-w-md">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search notices..."
          data-testid="notice-search-input"
          className="h-12 w-full rounded-xl border border-gold/20 bg-obsidian-card pl-12 pr-4 text-slate-100 outline-none focus:border-gold/60"
        />
      </div>

      <div className="mt-10 space-y-5">
        {isLoading && <p className="text-center text-slate-500">Loading...</p>}
        {!isLoading && filtered.length === 0 && (
          <p className="text-center text-slate-500" data-testid="notice-empty">No notices found.</p>
        )}
        {filtered.map((n) => (
          <div key={n.id} className="card-surface overflow-hidden rounded-2xl" data-testid={`notice-item-${n.id}`}>
            <div className="flex flex-col sm:flex-row">
              {n.image_url && (
                <img src={resolveImage(n.image_url)} alt={n.title} className="h-48 w-full object-cover sm:h-auto sm:w-56" />
              )}
              <div className="flex-1 p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold">
                    <Megaphone className="h-3.5 w-3.5" /> {n.category}
                  </span>
                  {n.date && (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="h-3.5 w-3.5" /> {n.date}
                    </span>
                  )}
                </div>
                <h3 className="mt-3 text-xl font-bold text-slate-100">{n.title}</h3>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-400">{n.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
